// src/pages/admin/VendorManagement/TypeChangeRequests.tsx

import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Check, X, FileText, Building2, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminAPI } from '../../../api/admin';
import TypeChangeStatusBadge from '../../../components/admin/TypeChangeStatusBadge';
import ReviewTypeChangeModal from '../../../components/admin/ReviewTypeChangeModal';
import TypeChangeDetailsModal from '../../../components/admin/TypeChangeDetailsModal';
import { useDebounce } from '../../../hooks/useDebounce';
import ReviewButton from '../../../components/admin/ReviewButton';

interface TypeChangeRequest {
    id: number;
    vendor: {
        id: number;
        business_name: string;
        business_email: string;
    };
    current_type: string;
    requested_type: string;
    reason: string;
    documents: { name: string; path: string; size: number }[];
    status: 'pending' | 'approved' | 'rejected';
    requested_at: string;
    reviewed_at?: string;
    reviewed_by?: string;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

const TypeChangeRequests: React.FC = () => {
    const [requests, setRequests] = useState<TypeChangeRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [selectedRequest, setSelectedRequest] = useState<TypeChangeRequest | null>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        pages: 0,
        pageSize: 20,
    });
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    useEffect(() => {
        fetchRequests();
    }, [debouncedSearchQuery, statusFilter, pagination.page]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await adminAPI.getVendorTypeChangeRequests(params);

            setRequests(Array.isArray(response.results) ? response.results : []);
            setPagination({
                page: pagination.page,
                total: response.results?.length || 0,
                pages: Math.ceil((response.results?.length || 0) / pagination.pageSize),
                pageSize: pagination.pageSize,
            });
        } catch (error) {
            console.error('Failed to fetch type change requests:', error);
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const handleReview = (request: TypeChangeRequest) => {
        setSelectedRequest(request);
        setShowReviewModal(true);
    };

    const handleViewDetails = (request: TypeChangeRequest) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleReviewComplete = async (action: 'approve' | 'reject', reason?: string) => {
        if (!selectedRequest) return;

        setIsProcessing(true);
        try {
            await adminAPI.reviewVendorTypeChange(selectedRequest.id, { action, reason });
            await fetchRequests();
            setShowReviewModal(false);
            setSelectedRequest(null);
        } catch (error) {
            console.error('Failed to review request:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const filteredRequests = requests.filter(request => {
        const matchesSearch = request.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            request.vendor_email.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatType = (type: string) => {
        const types: Record<string, string> = {
            individual: 'Individual',
            sole_proprietor: 'Sole Proprietor',
            llc: 'LLC',
            corporation: 'Corporation',
            partnership: 'Partnership',
            non_profit: 'Non-Profit',
        };
        return types[type] || type;
    };

    const filterOptions = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <p className="text-sm text-gray-500">Review and manage vendor type change requests</p>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by business name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    <Filter className="h-4 w-4" />
                    {statusFilter !== 'all' && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    {filterOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => {
                                setStatusFilter(opt.value);
                                setShowFilters(false);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === opt.value
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : filteredRequests.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No type change requests found</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Vendor</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Current Type</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Requested Type</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredRequests.map((request) => (
                                    <tr key={request.id} className="hover:bg-gray-50 transition-colors cursor-pointer"
                                        onClick={() => handleReview(request)}>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{request.vendor_name}</p>
                                                <p className="text-xs text-gray-500">{request.vendor_email}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {formatType(request.current_type)}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-blue-600">
                                            {formatType(request.requested_type)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {formatDate(request.requested_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <TypeChangeStatusBadge status={request.status} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {request.status === 'pending' ? (
                                                    <ReviewButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleReview(request);
                                                        }}
                                                        label="Review"
                                                        variant="primary"
                                                    />
                                                ) : (
                                                    <button
                                                        onClick={() => handleViewDetails(request)}
                                                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Results count */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                        Showing {requests.length} request {requests.length !== 1 ? 's' : ''}
                        {searchQuery ? `matching "${searchQuery}"` : ''}
                        of {pagination.total} total
                    </div>
                </div>
            )}
            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-gray-500">
                        Showing {requests.length} of {pagination.total} requests
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page <= 1}
                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {pagination.page} of {pagination.pages}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page >= pagination.pages}
                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showReviewModal && selectedRequest && (
                <ReviewTypeChangeModal
                    isOpen={showReviewModal}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedRequest(null);
                    }}
                    onConfirm={handleReviewComplete}
                    request={selectedRequest}
                    isProcessing={isProcessing}
                />
            )}

            {showDetailsModal && selectedRequest && (
                <TypeChangeDetailsModal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                    }}
                    request={selectedRequest}
                />
            )}
        </div>
    );
};

export default TypeChangeRequests;